import React, { useEffect } from 'react';
import { Form, Field } from 'react-final-form';
import { CheckboxField, TextField } from '../../atoms';
import { getLoadingSelector } from '../../utils/asyncHelpers';
import { Row, Col, Button, Spin, notification } from 'antd';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormActions } from '../../components';
import { FORM_CLASSIFIERS_COUNTRIES_EDIT } from '../../constants/formKeys';
import {
  getClassifiersCountriesChange,
  getClassifiersCountriesItem,
  getClassifiersCountriesItemData,
} from '../../reducers/classifiersReducer';
import * as actions from '../../actions/actionCreators/classifiersActionCreators';
import { countries } from '../../constants/parameters';
import validateSchema from './validateSchema';
import classifiersStore from '../../utils/classifiersStore';
import FormStateToRedux from '../FormStateToRedux';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';

const CountriesEdit = ({
  t,
  fetchClassifierCountries,
  changeClassifierCountries,
  fetchClassifierCountriesList,
  isLoading,
  operatorCode,
  onCancel,
  onComplete,
  initialValues: initialValuesProp,
}) => {
  useEffect(() => {
    if (operatorCode) {
      fetchClassifierCountries(operatorCode);
    }
  }, [operatorCode, fetchClassifierCountries]);

  // Convert Immutable Map to plain JS
  const initialValues = initialValuesProp?.toJS
    ? initialValuesProp.toJS()
    : initialValuesProp || {
        [countries.CODE]: '',
        [countries.ESTONIAN]: '',
        [countries.ENGLISH]: '',
      };

  const validate = makeRffValidateFromLegacy(
    validateSchema,
    { t },
    initialValues
  );

  const onSubmit = async (values) => {
    const result = await changeClassifierCountries(values);

    if (!result.error) {
      notification.info({
        message: t('completeTitle'),
        description: t('completeBody'),
      });

      onComplete();
    }

    await fetchClassifierCountriesList();
    await classifiersStore.update();
  };

  return (
    <Spin spinning={isLoading}>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <FormStateToRedux form={FORM_CLASSIFIERS_COUNTRIES_EDIT} />
            <Row gutter={32}>
              <Col span={24}>
                <Field
                  name={countries.CODE}
                  label={t('fieldCode')}
                  component={TextField}
                  disabled={!!operatorCode}
                  required
                />
              </Col>
            </Row>
            <Row gutter={32}>
              <Col span={12}>
                <Field
                  name={countries.ENGLISH}
                  label={t('fieldEn')}
                  component={TextField}
                  required
                />
              </Col>
              <Col span={12}>
                <Field
                  name={countries.ESTONIAN}
                  label={t('fieldEt')}
                  component={TextField}
                  required
                />
              </Col>
            </Row>
            <Row gutter={32}>
              <Col span={12}>
                <Field
                  name={countries.EXPORT}
                  type="checkbox"
                  component={CheckboxField}
                  checkboxText={t('countries.export')}
                  hasFeedback={false}
                  className="filter-checkbox"
                />
              </Col>
            </Row>
            <FormActions>
              <Button onClick={onCancel}>{t('buttonCancel')}</Button>
              <Button htmlType="submit" type="primary">
                {t('buttonSave')}
              </Button>
            </FormActions>
          </form>
        )}
      />
    </Spin>
  );
};

CountriesEdit.propTypes = {
  t: PropTypes.func.isRequired,
  fetchClassifierCountries: PropTypes.func.isRequired,
  changeClassifierCountries: PropTypes.func.isRequired,
  fetchClassifierCountriesList: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  operatorCode: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

const getIsLoading = getLoadingSelector(
  getClassifiersCountriesChange,
  getClassifiersCountriesItem
);

const mapStateToProps = (state, ownProps) => ({
  initialValues: ownProps.operatorCode
    ? getClassifiersCountriesItemData(state)
    : null,
  isLoading: getIsLoading(state),
});

const mapDispatchToProps = {
  fetchClassifierCountries: actions.fetchClassifierCountries,
  changeClassifierCountries: actions.changeClassifierCountries,
  fetchClassifierCountriesList: actions.fetchClassifierCountriesList,
};

export default connect(mapStateToProps, mapDispatchToProps)(CountriesEdit);
